import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { GetSingleFileDownload, GetMultiFileDownload } from 'api/appdownload.js';
import CircularProgress from '@mui/material/CircularProgress';
import {
  Backdrop,
  Box,
  Checkbox,
  Button,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Skeleton,
} from '@mui/material';
import { alpha, styled } from '@mui/material/styles';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import Alert from '@mui/material/Alert';
import { fetchSignedURL } from '../../../../../api/file';

const StyledDownloadContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '.5rem',
  padding: '1rem',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

export const AppList = ({
  setSnackbarDetails,
  producerID,
  user,
  isLoading,
  maxPolicyDownloadCount,
  appList,
}) => {
  const [policies, setPolicies] = useState([]); //used for checkbox selection
  // const [allPolicies, setAllPolicies] = useState([]); //used for all download

  const getProducerID = () => (producerID !== '' ? producerID : user.producerID.split(',')[0]);

  // Downloads are mutations (not queries) so a result is only ever acted on in response to a
  // button click; cached query data would otherwise re-trigger the save dialog on checkbox changes.
  const handleDownloadResult = (data) => {
    if (!data) return;
    if (data === 'No Access to PolicyID') {
      setSnackbarDetails({
        open: true,
        status: 'error',
        message: 'No accesss to policy ID.',
      });
      return;
    }
    fetchSignedURL(data);
  };

  //single print
  // (isBusy is defined below the mutations)
  const downloadAppFile = useMutation({
    mutationFn: (policyID) =>
      GetSingleFileDownload({
        PolicyID: policyID,
        ProducerID: getProducerID(),
      })(),
    onSuccess: handleDownloadResult,
  });

  //multiprint
  const downloadMultiFiles = useMutation({
    mutationFn: (selectedPolicies) =>
      GetMultiFileDownload({
        ProducerID: getProducerID(),
        MultipleFiles: selectedPolicies,
      })(),
    onSuccess: handleDownloadResult,
  });

  const isBusy = downloadAppFile.isPending || downloadMultiFiles.isPending;

  //handle checkbox select/unselect
  const handleChange = (e) => {
    const { value, checked } = e.target;

    //checked add to list
    if (checked) {
      setPolicies((policies) => [...policies, value]);
    }

    //uncheck remove from list
    else {
      var filterList = policies.filter((item) => item !== value);
      setPolicies(filterList);
    }
  };

  //single print call
  function DownloadSingleApp(val) {
    setSnackbarDetails({
      open: false,
      status: '',
      message: '',
    });
    downloadAppFile.mutate(val);
  }

  //multiprint call
  function DownloadMultiApps() {
    setSnackbarDetails({
      open: false,
      status: '',
      message: '',
    });
    downloadMultiFiles.mutate(policies);
  }

  /*function DownloadAllApps() {
        var newAr = appList.map(a => a.policyID);
        setAllPolicies(allPolicies => newAr);
    }*/

  return (
    <>
      <Backdrop
        open={isBusy}
        sx={{
          color: '#fff',
          backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.35),
          zIndex: (theme) => theme.zIndex.modal + 1,
          flexDirection: 'column',
          gap: '1.5rem',
        }}
      >
        <CircularProgress color="inherit" size={64} />
        <Box
          sx={{
            fontSize: '1.5rem',
            fontWeight: 'bold',
            lineHeight: 1.4,
            textAlign: 'center',
            maxWidth: '40rem',
            padding: '0 1rem',
          }}
        >
          {downloadMultiFiles.isPending
            ? 'Generating Zip File...This could take a while. Please do not navigate away from this page or close the browser window.'
            : 'Preparing your download...'}
        </Box>
      </Backdrop>
      <StyledDownloadContainer>
        <Alert severity="info" style={{ width: 'auto', backgroundColor: 'rgb(229, 246, 253)' }}>
          You can only download a <b>Max</b> of <b>{maxPolicyDownloadCount} Policies</b> at one
          time.
        </Alert>

        <Button
          disabled={isBusy || policies.length > maxPolicyDownloadCount || policies.length <= 0}
          size="medium"
          variant="contained"
          onClick={() => {
            DownloadMultiApps();
          }}
        >
          {downloadMultiFiles.isPending || downloadAppFile.isPending ? (
            <CircularProgress size={16} />
          ) : (
            'Download Selected'
          )}
        </Button>
        <div style={{ fontWeight: 'bold', color: 'red' }}>
          {downloadMultiFiles.isPending
            ? 'Generating Zip File...This could take a while.  Please do not navigate away from this page or close the browser window.'
            : ''}
        </div>
      </StyledDownloadContainer>

      <TableContainer>
        <Table aria-label="customized table">
          <TableHead>
            <TableRow>
              <StyledTableCell></StyledTableCell>
              <StyledTableCell>Policy ID</StyledTableCell>
              <StyledTableCell>Applicant Name</StyledTableCell>
              <StyledTableCell>Producer ID</StyledTableCell>
              <StyledTableCell>Agent Name</StyledTableCell>
              <StyledTableCell></StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <>
                {Array.from({ length: 5 }, (_, index) => (
                  <StyledTableRow key={index}>
                    {Array.from({ length: 6 }, (_, itemIndex) => (
                      <StyledTableCell sx={{ padding: '0' }} key={itemIndex}>
                        <Skeleton
                          variant="rectangular"
                          sx={{ width: '100%', height: '4rem', backgroundColor: 'none' }}
                        />
                      </StyledTableCell>
                    ))}
                  </StyledTableRow>
                ))}
              </>
            ) : (
              <>
                {appList.map((e, i) => {
                  return (
                    <StyledTableRow key={i + 'row'}>
                      <StyledTableCell>
                        <Checkbox
                          type="checkbox"
                          name={'chx' + i}
                          value={e.policyID}
                          disabled={isBusy}
                          onChange={handleChange}
                          color="primary"
                        />
                      </StyledTableCell>
                      <StyledTableCell>{e.policyID}</StyledTableCell>
                      <StyledTableCell>{e.applicantName}</StyledTableCell>
                      <StyledTableCell>{e.producerID}</StyledTableCell>
                      <StyledTableCell>{e.agentName}</StyledTableCell>
                      <StyledTableCell>
                        <div>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => {
                              DownloadSingleApp(e.policyID);
                            }}
                          >
                            {downloadAppFile.isPending || downloadMultiFiles.isPending ? (
                              <CircularProgress size={16} />
                            ) : (
                              'Print'
                            )}
                          </Button>
                        </div>
                      </StyledTableCell>
                    </StyledTableRow>
                  );
                })}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};
